<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { artifactUrl, getChip, getProvenance } from "../api";
import EntityHeader from "../components/EntityHeader.vue";
import MemoryMap from "../components/MemoryMap.vue";
import ProvenanceList from "../components/ProvenanceList.vue";
import type { Chip, ProvenanceItem } from "../types";
const route = useRoute();
const chip = ref<Chip>();
const provenance = ref<ProvenanceItem[]>([]);
const view = ref("");
const release = computed(() => String(route.params.release));
const socName = computed(() => {
  const family = chip.value?.family;
  if (family === "imx95") return "i.MX 95";
  if (family === "imx93") return "i.MX 93";
  return family?.toUpperCase() ?? "SoC";
});
const coreName = computed(() => chip.value?.address_spaces[0]?.toUpperCase() ?? "");
async function load() {
  const revision = typeof route.query.revision === "string" ? route.query.revision : undefined;
  chip.value = await getChip(release.value, String(route.params.entity), revision);
  view.value = chip.value.address_spaces[0] ?? "";
  provenance.value = await getProvenance(
    release.value,
    chip.value.entity_id,
    chip.value.revision_id,
  );
}
onMounted(() => void load());
watch(
  () => route.fullPath,
  () => void load(),
);
</script>
<template>
  <div v-if="chip" class="page-shell">
    <nav class="breadcrumbs" aria-label="Breadcrumb">
      <RouterLink to="/">Catalog</RouterLink><span>/</span><span>Chips</span><span>/</span
      ><strong>{{ chip.name }}</strong>
    </nav>
    <EntityHeader
      :eyebrow="`SoC · ${coreName}`"
      :title="chip.name"
      :description="`${chip.vendor} ${chip.family} heterogeneous processor`"
      :revision="chip.revision_id"
    />
    <div class="fact-strip">
      <div>
        <span>Vendor</span><strong>{{ chip.vendor }}</strong>
      </div>
      <div>
        <span>SoC</span><strong>{{ socName }}</strong>
      </div>
      <div>
        <span>Core</span><strong>{{ coreName }}</strong>
      </div>
      <div>
        <span>Aliases</span><strong>{{ chip.aliases.join(", ") }}</strong>
      </div>
    </div>
    <section class="section-block">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Execution context</p>
          <h2>Address-space view</h2>
        </div>
        <div class="segmented">
          <button
            v-for="space in chip.address_spaces"
            :key="space"
            :class="{ active: view === space }"
            @click="view = space"
          >
            {{ space.toUpperCase() }}
          </button>
        </div>
      </div>
      <MemoryMap :regions="chip.memory_regions" :address-space="view" />
    </section>
    <section class="section-block">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Mapped IP</p>
          <h2>Peripheral instances</h2>
        </div>
        <span class="count"
          >{{
            chip.peripheral_instances.filter((i) =>
              i.mappings.some((m) => m.address_space === view),
            ).length
          }}
          visible</span
        >
      </div>
      <div class="data-table" role="table">
        <div class="table-head" role="row">
          <span>Instance</span><span>Peripheral</span><span>Base</span><span>Size</span>
        </div>
        <RouterLink
          v-for="instance in chip.peripheral_instances.filter((i) =>
            i.mappings.some((m) => m.address_space === view),
          )"
          :key="instance.name"
          :to="{
            path: `/releases/${release}/peripherals/${instance.peripheral}`,
            query: { revision: instance.peripheral_revision },
          }"
          class="table-row"
          role="row"
          ><strong>{{ instance.name }}</strong
          ><span>{{ instance.peripheral_name ?? "Peripheral IP" }}</span
          ><code>{{ instance.mappings.find((m) => m.address_space === view)?.base }}</code
          ><code>{{
            instance.mappings.find((m) => m.address_space === view)?.size
          }}</code></RouterLink
        >
      </div>
    </section>
    <section class="section-block">
      <div class="section-heading">
        <div>
          <p class="eyebrow">Outputs</p>
          <h2>Exact release artifacts</h2>
        </div>
      </div>
      <div class="download-grid">
        <a
          v-for="format in ['svd', 'chiptool', 'c-header', 'rust-pac']"
          :key="format"
          :href="
            artifactUrl(
              release,
              format,
              `${chip.name}-${view}.${format === 'c-header' ? 'h' : format === 'svd' ? 'svd' : format === 'rust-pac' ? 'rs' : 'yaml'}`,
            )
          "
          ><span>{{ format }}</span
          ><strong>Download ↘</strong></a
        >
      </div>
    </section>
    <ProvenanceList :items="provenance" />
  </div>
  <p v-else class="page-loading">Loading chip revision…</p>
</template>
